import { useState } from "react";
import { toFriendlyMutationError } from "@/lib/error-utils";

export type MutationOptions<T> = { onSuccess?: (data: T) => void };

export type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };

export type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};

export function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

export async function reportServerMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult.catch((error) => {
    throw toFriendlyMutationError(error);
  });
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function usePendingMutation() {
  const [isPending, setIsPending] = useState(false);

  const runPending = async <T>(operation: () => Promise<T>) => {
    setIsPending(true);
    try {
      return await operation();
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };

  return { isPending, runPending };
}
